// ─── Academic Completeness Validator ─────────────────────────────────────────
// Semantic validation of thesis academic structure.
// Runs AFTER the syntax/placeholder validator and checks whether each chapter
// fulfils its academic function.

// ── Keyword sets for heuristic semantic detection ────────────────────────────

const ABSTRACT_REQUIRED = {
  problem:      ['problem', 'gap', 'challenge', 'issue', 'limitation', 'inadequacy', 'lacuna', 'deficiency'],
  methodology:  ['methodology', 'method', 'approach', 'design', 'sample', 'data collection', 'survey', 'interview', 'mixed method', 'qualitative', 'quantitative', 'analysis'],
  findings:     ['finding', 'result', 'reveal', 'indicate', 'demonstrate', 'show', 'discover', 'establish', 'suggest'],
  contribution: ['contribution', 'contribute', 'novel', 'original', 'advance', 'extend', 'add to', 'fill', 'bridge', 'implication'],
}

const INTRO_REQUIRED = {
  context:     ['context', 'background', 'landscape', 'overview', 'field of', 'domain of', 'area of'],
  gap:         ['gap', 'limited research', 'under-explored', 'insufficient', 'lack of', 'absent from', 'overlooked', 'under-studied'],
  objectives:  ['objective', 'aim', 'goal', 'purpose', 'seek to', 'intend to', 'set out to', 'this study', 'research question'],
  overview:    ['structure', 'chapter', 'organised as', 'remainder', 'thesis is divided', 'overview of'],
}

const LITREV_REQUIRED = {
  synthesis:      ['synthesis', 'collectively', 'across the literature', 'body of research', 'scholars suggest', 'dominant view'],
  critique:       ['however', 'critique', 'criticise', 'limitation of', 'weakness', 'gap in', 'fails to', 'overlooks', 'argue against'],
  theoretical:    ['theoretical framework', 'theoretical lens', 'theoretical perspective', 'underpinned by', 'grounded in theory', 'conceptual framework'],
  gap_discussion: ['gap in the literature', 'research gap', 'unexplored', 'absent from the literature', 'this study addresses'],
}

const METHODOLOGY_REQUIRED = {
  sample:      ['sample', 'participant', 'respondent', 'n =', 'n=', 'interviews', 'population', 'sampling'],
  collection:  ['data collection', 'interview', 'survey', 'questionnaire', 'observation', 'document analysis', 'secondary data', 'primary data'],
  justification:['justification', 'rationale', 'chosen because', 'appropriate because', 'suited to', 'enables', 'allows'],
  limitations: ['limitation', 'constraint', 'boundary', 'scope', 'assumption', 'delimitation'],
}

const FINDINGS_REQUIRED = {
  findings:        ['finding', 'result', 'data reveal', 'analysis show', 'emerged', 'identified', 'demonstrate', 'indicate'],
  interpretation:  ['suggest', 'imply', 'indicate', 'therefore', 'thus', 'consequently', 'in light of', 'this means'],
}

const DISCUSSION_REQUIRED = {
  reconciliation: ['align with', 'consistent with', 'support', 'contradict', 'contrast with', 'differ from', 'corroborate', 'extends'],
  literature_link:['prior research', 'previous study', 'literature suggest', 'scholar', 'existing', 'according to'],
}

const CONCLUSION_REQUIRED = {
  contribution: ['contribution', 'contribute', 'advance', 'implication', 'theoretical contribution', 'practical implication'],
  limitations:  ['limitation', 'constraint', 'scope'],
  future_work:  ['future research', 'future study', 'further research', 'recommend', 'subsequent'],
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function stripHtml(html) {
  return (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').toLowerCase().trim()
}

function countKeywords(text, keywords) {
  return keywords.filter(kw => text.includes(kw)).length
}

function checkComponents(text, required, threshold = 1) {
  const result = {}
  let found = 0
  for (const [component, keywords] of Object.entries(required)) {
    const hits = countKeywords(text, keywords)
    result[component] = { present: hits >= threshold, hits }
    if (hits >= threshold) found++
  }
  result._score = Math.round((found / Object.keys(required).length) * 100)
  return result
}

function chapterRole(ch) {
  const title = (ch.title || '').toLowerCase()
  const num   = ch.chapter_number || 0

  if (title.includes('abstract'))                                    return 'abstract'
  if (title.includes('introduction') || num === 1)                  return 'introduction'
  if (title.includes('literature') || title.includes('review'))     return 'literature_review'
  if (title.includes('methodolog') || title.includes('research design')) return 'methodology'
  if (title.includes('finding') || title.includes('result') || title.includes('data analysis')) return 'findings'
  if (title.includes('discussion') || title.includes('analysis'))   return 'discussion'
  if (title.includes('conclusion') || title.includes('recommendation')) return 'conclusion'
  return 'body'
}

// ── Chapter-level semantic checks ─────────────────────────────────────────────

function validateChapterRole(ch) {
  const role = chapterRole(ch)
  const text = stripHtml(ch.content || '')
  const wc   = text.split(/\s+/).filter(Boolean).length
  const result = { role, chapterNumber: ch.chapter_number, title: ch.title, wordCount: wc }

  switch (role) {
    case 'abstract':
      result.components = checkComponents(text, ABSTRACT_REQUIRED)
      result.minWords   = 150
      break
    case 'introduction':
      result.components = checkComponents(text, INTRO_REQUIRED)
      result.minWords   = 1200
      break
    case 'literature_review':
      result.components = checkComponents(text, LITREV_REQUIRED)
      result.minWords   = 3000
      break
    case 'methodology':
      result.components = checkComponents(text, METHODOLOGY_REQUIRED)
      result.minWords   = 2000
      break
    case 'findings':
      result.components = checkComponents(text, FINDINGS_REQUIRED)
      result.minWords   = 2500
      break
    case 'discussion':
      result.components = checkComponents(text, DISCUSSION_REQUIRED)
      result.minWords   = 2000
      break
    case 'conclusion':
      result.components = checkComponents(text, CONCLUSION_REQUIRED)
      result.minWords   = 800
      break
    default:
      result.components = { _score: 100 }
      result.minWords   = 500
  }

  result.isTooShort   = wc < result.minWords
  result.componentScore = result.components._score ?? 100
  return result
}

// ── Figure integrity checks ───────────────────────────────────────────────────

/**
 * Validate figure integrity across chapters.
 * Checks orphan tokens, missing references, numbering gaps, chapter mismatches.
 */
export function validateFigureIntegrity(chapters, figures, tables) {
  const issues = []

  // 1. Figures in registry that appear in wrong chapter
  for (const [, fig] of figures) {
    if (fig.chapterNum && !fig.figureNumber.startsWith(`${fig.chapterNum}.`)) {
      issues.push({
        type:    'CHAPTER_MISMATCH',
        message: `${fig.displayLabel} is in Chapter ${fig.chapterNum} but numbered differently.`,
        token:   fig.token,
      })
    }
  }

  // 2. Numbering gaps — detect missing sequence numbers per chapter
  const byChapter = new Map()
  for (const [, fig] of figures) {
    const ch = fig.chapterNum
    if (!byChapter.has(ch)) byChapter.set(ch, [])
    const seq = parseInt(fig.figureNumber.split('.')[1] || '0', 10)
    byChapter.get(ch).push(seq)
  }
  for (const [ch, seqs] of byChapter) {
    const sorted = [...seqs].sort((a, b) => a - b)
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] !== sorted[i - 1] + 1) {
        issues.push({
          type:    'NUMBERING_GAP',
          message: `Chapter ${ch} figures have a numbering gap between Figure ${ch}.${sorted[i-1]} and Figure ${ch}.${sorted[i]}.`,
        })
      }
    }
  }

  // 3. Empty captions
  for (const [, fig] of figures) {
    if (!fig.caption) {
      issues.push({
        type:    'EMPTY_CAPTION',
        message: `${fig.displayLabel}: "${fig.title || '(untitled)'}" has no caption.`,
        token:   fig.token,
      })
    }
  }
  for (const [, tbl] of tables) {
    if (!tbl.caption) {
      issues.push({
        type:    'EMPTY_TABLE_CAPTION',
        message: `${tbl.displayLabel}: "${tbl.title || '(untitled)'}" has no caption.`,
        token:   tbl.token,
      })
    }
  }

  // 4. Duplicate captions
  const captions = new Map()
  for (const [, fig] of figures) {
    const cap = (fig.caption || '').trim().toLowerCase()
    if (cap.length < 10) continue
    if (captions.has(cap)) {
      issues.push({ type: 'DUPLICATE_CAPTION', message: `Duplicate caption between ${fig.displayLabel} and ${captions.get(cap)}.`, token: fig.token })
    } else {
      captions.set(cap, fig.displayLabel)
    }
  }

  // 5. Figure integrity score
  const totalFigures = figures.size + tables.size
  const penaltyPer   = totalFigures > 0 ? Math.round(100 / Math.max(totalFigures, 1)) : 0
  const score        = Math.max(0, 100 - issues.length * penaltyPer)

  return { issues, figureIntegrityScore: score, totalFigures }
}

// ── Citation confidence engine ────────────────────────────────────────────────

/**
 * Score citation quality across chapters.
 * Heuristic-based: checks density, suspicious years, repetition, hallucination patterns.
 */
export function computeCitationConfidence(chapters) {
  const currentYear  = new Date().getFullYear()
  const suspiciousCitations = []
  const weakSections = []
  let totalCitations = 0
  let suspiciousCount = 0

  // Citation patterns: APA (Author, Year) and IEEE [N]
  const APA_RE = /\(([A-Z][a-z]+(?:\s+et\s+al\.)?(?:,?\s+[A-Z][a-z]+)*)?,?\s*(\d{4}[a-z]?)\)/g
  const IEEE_RE = /\[(\d+)\]/g

  const hallucination_names = [
    'smith and johnson', 'johnson and smith', 'jones et al', 'brown and white',
    'wilson et al', 'taylor and jones', 'anderson et al',
  ]
  const suspiciousNamePatterns = hallucination_names.map(n => new RegExp(n, 'i'))

  for (const ch of chapters) {
    const text = (ch.content || '').replace(/<[^>]+>/g, ' ')
    const wc   = text.split(/\s+/).length

    const apaCites  = [...text.matchAll(APA_RE)]
    const ieeeCites = [...text.matchAll(IEEE_RE)]
    const citeCount = apaCites.length + ieeeCites.length
    totalCitations += citeCount

    const citeDensity = wc > 0 ? citeCount / (wc / 250) : 0 // cites per 250 words

    // Weak if long chapter but very few citations
    if (wc > 1000 && citeDensity < 0.5 && ch.chapter_number > 1) {
      weakSections.push({
        chapter: `Chapter ${ch.chapter_number}: ${ch.title}`,
        density: Math.round(citeDensity * 10) / 10,
        wordCount: wc,
      })
    }

    // Check for suspicious years
    for (const cite of apaCites) {
      const year = parseInt(cite[2], 10)
      if (year > currentYear || year < 1960) {
        suspiciousCitations.push({
          citation: cite[0],
          chapter:  `Chapter ${ch.chapter_number}`,
          reason:   year > currentYear ? 'Future year' : 'Very old (pre-1960)',
        })
        suspiciousCount++
      }
      // Hallucination name patterns
      const authorStr = (cite[1] || '').toLowerCase()
      for (const re of suspiciousNamePatterns) {
        if (re.test(authorStr)) {
          suspiciousCitations.push({
            citation: cite[0],
            chapter:  `Chapter ${ch.chapter_number}`,
            reason:   'Common hallucination name pattern',
          })
          suspiciousCount++
          break
        }
      }
    }
  }

  // Compute confidence score
  const suspiciousRatio   = totalCitations > 0 ? suspiciousCount / totalCitations : 0
  const weakSectionPenalty = weakSections.length * 5
  const confidenceScore   = Math.max(0, Math.round(100 - suspiciousRatio * 100 - weakSectionPenalty))

  return {
    confidenceScore,
    totalCitations,
    suspiciousCitations: suspiciousCitations.slice(0, 20), // cap for display
    weakSections,
    unsupportedClaims: [], // would require full LLM analysis
  }
}

// ── Main academic validator ───────────────────────────────────────────────────

/**
 * Run semantic academic completeness validation.
 *
 * @param {{ chapters: Array, fm: object }} data
 * @returns {AcademicValidationResult}
 */
export function validateAcademicCompleteness({ chapters, fm }) {
  if (!chapters || chapters.length === 0) {
    return {
      completenessScore: 0,
      missingSections:   ['No chapters found'],
      weakSections:      [],
      structuralWarnings: [],
      academicWarnings:  [],
      chapterResults:    [],
      citationConfidence: null,
    }
  }

  const sorted = [...chapters].sort((a, b) => (a.chapter_number || 0) - (b.chapter_number || 0))

  // Validate each chapter by role
  const chapterResults = sorted.map(ch => validateChapterRole(ch))

  // Identify missing key roles
  const roles = new Set(chapterResults.map(r => r.role))
  const requiredRoles = ['introduction', 'literature_review', 'methodology', 'findings', 'conclusion']
  const missingSections = requiredRoles
    .filter(r => !roles.has(r))
    .map(r => r.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))

  // Identify weak chapters
  const weakSections = chapterResults
    .filter(r => r.role !== 'body' && (r.componentScore < 60 || r.isTooShort))
    .map(r => ({
      chapter:        `Chapter ${r.chapterNumber}: ${r.title}`,
      role:           r.role,
      componentScore: r.componentScore,
      wordCount:      r.wordCount,
      minWords:       r.minWords,
      shortfall:      r.isTooShort ? r.minWords - r.wordCount : 0,
      missingComponents: Object.entries(r.components || {})
        .filter(([k, v]) => k !== '_score' && !v.present)
        .map(([k]) => k.replace(/_/g, ' ')),
    }))

  const structuralWarnings = []
  if (chapters.length < 5) structuralWarnings.push(`Only ${chapters.length} chapter(s) — PhD thesis typically requires at least 5.`)
  const totalWords = chapters.reduce((s, c) => s + (c.word_count || 0), 0)
  if (totalWords < 40000) structuralWarnings.push(`Total word count (${totalWords.toLocaleString()}) is below PhD minimum (typically 70,000–100,000 words).`)

  const academicWarnings = []
  if (!fm?.abstract_text || fm.abstract_text.trim().length < 100)
    academicWarnings.push('Abstract is missing or too short (< 100 characters).')
  if (!fm?.keywords || fm.keywords.length < 3)
    academicWarnings.push('Keywords are missing or too few (recommend 5–8).')

  // Citation confidence
  const citationConfidence = computeCitationConfidence(sorted)

  // Overall completeness score
  const avgComponentScore = chapterResults.length > 0
    ? chapterResults.reduce((s, r) => s + (r.componentScore ?? 100), 0) / chapterResults.length
    : 0
  const sectionPenalty    = missingSections.length * 10
  const weakPenalty       = weakSections.length * 5
  const completenessScore = Math.max(0, Math.round(avgComponentScore - sectionPenalty - weakPenalty))

  return {
    completenessScore,
    missingSections,
    weakSections,
    structuralWarnings,
    academicWarnings,
    chapterResults,
    citationConfidence,
  }
}
